import * as React from "react";
import { ModalLayout } from "../layout";
import changelogRaw from '../data/CHANGELOG.md';

const CHANGELOG_STORAGE_KEY = "changelogState";
const CURRENT_CHANGELOG_VERSION = "2026-02-15";

interface ChangelogState {
  lastSeenVersion: string | null;
}

const getStoredState = (): ChangelogState => {
  try {
    const stored = localStorage.getItem(CHANGELOG_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {}
  return { lastSeenVersion: null };
};

const setStoredState = (state: ChangelogState) => {
  try {
    localStorage.setItem(CHANGELOG_STORAGE_KEY, JSON.stringify(state));
  } catch (e) {}
};

export const useChangelogNotification = () => {
  const [hasUnseenChanges, setHasUnseenChanges] = React.useState(false);

  React.useEffect(() => {
    const state = getStoredState();
    if (state.lastSeenVersion !== CURRENT_CHANGELOG_VERSION) {
      setHasUnseenChanges(true);
    }
  }, []);

  const markAsSeen = () => {
    setStoredState({ lastSeenVersion: CURRENT_CHANGELOG_VERSION });
    setHasUnseenChanges(false);
  };

  return { hasUnseenChanges, markAsSeen };
};

/**
 * Strips the top section if it starts with '# Changelog',
 * up to and including the first thematic hr ('---').
 * Returns the markdown string suitable for parsing.
 */
function getChangelogMarkdown(raw: string): string {
  const lines = raw.trim().split("\n");
  // Check for '# Changelog' at the top
  if (lines[0]?.startsWith("# Changelog")) {
    // Find the first thematic break (---), then skip everything up to and including it
    const firstHrIdx = lines.findIndex((l) => l.trim() === "---");
    if (firstHrIdx !== -1) {
      return lines
        .slice(firstHrIdx + 1)
        .join("\n")
        .trim();
    }
  }
  return raw.trim();
}

const changelogContent = getChangelogMarkdown(changelogRaw);

const parseMarkdown = (md: string): React.ReactNode[] => {
  const lines = md.trim().split("\n");
  const elements: React.ReactNode[] = [];
  let listItems: string[] = [];

  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(
        <ul
          key={`list-${elements.length}`}
          className="list-disc ml-6 mb-3 space-y-1"
        >
          {listItems.map((item, i) => {
            const boldMatch = item.match(/^\*\*(.+?)\*\*\s*[-–]?\s*(.*)$/);
            if (boldMatch) {
              return (
                <li key={i}>
                  <strong>{boldMatch[1]}</strong>
                  {boldMatch[2] && ` - ${boldMatch[2]}`}
                </li>
              );
            }
            return <li key={i}>{item}</li>;
          })}
        </ul>,
      );
      listItems = [];
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    if (line.startsWith("## ")) {
      flushList();
      elements.push(
        <h2
          key={`h2-${i}`}
          className="text-lg font-bold mt-4 mb-2 pb-1"
        >
          {line.replace("## ", "")}
        </h2>,
      );
    } else if (line.startsWith("### ")) {
      flushList();
      elements.push(
        <h3 key={`h3-${i}`} className="font-bold mt-3 mb-1">
          {line.replace("### ", "")}
        </h3>,
      );
    } else if (line.startsWith("- ")) {
      listItems.push(line.replace("- ", ""));
    } else if (line === "---") {
      flushList();
      elements.push(
        <hr
          key={`hr-${i}`}
          className="my-4 border-theme-color border-dashed"
        />,
      );
    } else if (line.trim() === "") {
      flushList();
    }
  }
  flushList();

  return elements;
};

interface ChangelogModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubscribe: () => void;
}

export const ChangelogModal: React.FC<ChangelogModalProps> = ({
  isOpen,
  onClose,
  onSubscribe,
}) => {
  const { markAsSeen } = useChangelogNotification();

  React.useEffect(() => {
    if (isOpen) {
      markAsSeen();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubscribeClick = () => {
    onSubscribe();
    // onClose();
  };

  return (
    <ModalLayout title="WHAT'S NEW" onClose={onClose}>
      <div className="max-h-[60vh] overflow-y-auto pr-2">
        <div className="text-sm">{parseMarkdown(changelogContent)}</div>
      </div>
      <div className="mt-4 pt-4 border-t border-dashed border-theme-color">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-sm">
            Want to know when we add new features and artifacts?
          </p>
          <button
            onClick={handleSubscribeClick}
            className="border-theme-color text-theme-color hover:bg-theme-color border p-2 px-4 border-dashed font-bold flex items-center gap-2"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
            SUBSCRIBE FOR UPDATES
          </button>
        </div>
      </div>
    </ModalLayout>
  );
};

interface ChangelogMenuItemProps {
  onClick: () => void;
  hasNotification: boolean;
}

export const ChangelogMenuItem: React.FC<ChangelogMenuItemProps> = ({
  onClick,
  hasNotification,
}) => {
  return (
    <span className="relative inline-flex items-center">
      WHAT'S NEW
      {hasNotification && (
        <span className="absolute -top-1 -right-3 flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
        </span>
      )}
    </span>
  );
};
